import { apiSlice as api } from "@/store/rtk-api/apiSlice"

const injectedRtkApi = api.injectEndpoints({
  endpoints: (builder) => {
    const transformJourneyResponse = (response: SetupJourneyResponse): SetupJourney => {
      if (!response.success || !response.journey) {
        throw new Error(response.error || "Failed to fetch setup journey")
      }
      return response.journey
    }

    return {
      // Fetches the current climate-platform onboarding state for the tenant.
      // Use this to render step status, the active step, lock state, and current CTA.
      getOnboardingSetupJourney: builder.query<SetupJourney, void>({
        query: () => ({
          url: "/v1/onboarding/climate-platform/setup-journey",
          method: "GET",
        }),
        transformResponse: transformJourneyResponse,
        providesTags: ["SetupJourney"],
      }),
      // OPTIONAL: Recomputes onboarding from the latest saved tenant data and syncs onboarding audit milestones.
      // Optionally call this after onboarding-related mutations when the UI needs fresh synced journey state.
      setOnboardingSetupJourneySync: builder.mutation<SetupJourney, void>({
        query: () => ({
          url: "/v1/onboarding/climate-platform/setup-journey/sync",
          method: "POST",
        }),
        transformResponse: transformJourneyResponse,
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled
          dispatch(injectedRtkApi.util.updateQueryData("getOnboardingSetupJourney", undefined, () => data))
        },
      }),
      // Completes the related-entities step through the explicit "no related entities" path
      // and returns the updated onboarding journey state.
      setOnboardingSetupJourneyNoRelatedEntities: builder.mutation<SetupJourney, void>({
        query: () => ({
          url: "/v1/onboarding/climate-platform/setup-journey/related-entities/confirm-none",
          method: "POST",
        }),
        transformResponse: transformJourneyResponse,
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled
          dispatch(injectedRtkApi.util.updateQueryData("getOnboardingSetupJourney", undefined, () => data))
        },
      }),
    }
  },
})

export const {
  useGetOnboardingSetupJourneyQuery,
  useSetOnboardingSetupJourneySyncMutation,
  useSetOnboardingSetupJourneyNoRelatedEntitiesMutation,
} = injectedRtkApi

export default injectedRtkApi

// ─── Types ────────────────────────────────────────────────────────────────────

export type SetupJourneyStepKey = "PARENT_COMPANY" | "REPORTING_YEAR" | "RELATED_ENTITIES" | "LOCATIONS"

export type SetupJourneyStepStatus = "COMPLETED" | "ACTIVE" | "LOCKED"

export interface SetupJourneyStep {
  key: SetupJourneyStepKey
  order: number
  status: SetupJourneyStepStatus
}

export interface SetupJourney {
  setupComplete: boolean
  currentStep: SetupJourneyStepKey
  currentCta: {
    action: SetupJourneyStepKey
  }
  steps: SetupJourneyStep[]
}

interface SetupJourneyResponse {
  success: boolean
  error?: string
  journey: SetupJourney
}
